






















































import { Component, Ref, Vue } from "vue-property-decorator";
import BaseDialogComponent from "@/components/common/BaseDialog.vue";
import {
  checkTemplateFileName,
  MAX_TEMPLATE_FILE_BYTES,
  MAX_TEMPLATE_FILE_NAME_LENGTH,
  templateNameSwitches,
} from "@/utils/PdfTemplateFiles";

export type PdfTemplateFileMode = "upload" | "replace" | "rename" | "copy";

/** A template that can be copied: its id, what the list shows and its file name. */
export interface PdfTemplateCopySource {
  id: string;
  label: string;
  fileName: string;
}

export interface PdfTemplateFileDialogArgs {
  mode: PdfTemplateFileMode;
  title: string;
  /** The name the field starts with, without `.pdf`. */
  initialName: string;
  /** The target scope's current file names; IRES Cloud also refuses names of files in its folder. */
  takenFileNames: string[];
  /** What the action reaches, one sentence a line: shown in the dialog, so the button confirms it. */
  consequenceLines: string[];
  /** For a copy from the shared templates: the ones to choose from. Empty when the source is fixed. */
  sources?: PdfTemplateCopySource[];
}

export interface PdfTemplateFileDialogResult {
  fileName: string;
  file: File | null;
  sourceId: string | null;
}

/**
 * Uploading, replacing, renaming or copying a PDF template: a PDF (upload, replace), a source (a copy
 * from the shared templates), and a file name that must be new to the scope, because IRES Project keeps
 * a template's PDF on the tablet under its name and never fetches it again. The dialog says what the
 * action reaches, and its button carries out exactly that, so it needs no second confirmation.
 *
 * `show()` resolves with the choice, or null when cancelled.
 */
@Component({ components: { "base-dialog": BaseDialogComponent } })
export default class PdfTemplateFileDialogComponent extends Vue {
  @Ref("baseDialog")
  baseDialog!: BaseDialogComponent;

  mode: PdfTemplateFileMode = "upload";
  name = "";
  file: File | null = null;
  sourceId: string | null = null;
  sources: PdfTemplateCopySource[] = [];
  takenFileNames: string[] = [];
  consequenceLines: string[] = [];

  readonly maxNameLength = MAX_TEMPLATE_FILE_NAME_LENGTH;
  private resolve: ((result: PdfTemplateFileDialogResult | null) => void) | null = null;

  show(args: PdfTemplateFileDialogArgs): Promise<PdfTemplateFileDialogResult | null> {
    this.mode = args.mode;
    this.name = args.initialName;
    this.file = null;
    this.sources = args.sources ?? [];
    this.sourceId = null;
    this.takenFileNames = args.takenFileNames;
    this.consequenceLines = args.consequenceLines;
    // Persistent: an action that reaches IRES Project is finished or cancelled, never clicked away.
    // closeOnSave false: the button stays until the choice is complete (onConfirm closes it).
    this.baseDialog.show({
      title: args.title,
      positiveButtonText: this.$t(`admin.pdfTemplates.files.actions.${args.mode}`).toString(),
      negativeButtonText: this.$t("common.abort").toString(),
      maxWidth: 640,
      scrollable: true,
      persistent: true,
      closeOnSave: false,
    });

    return new Promise((resolve) => (this.resolve = resolve));
  }

  get takesFile(): boolean {
    return this.mode === "upload" || this.mode === "replace";
  }

  get nameCheck() {
    return checkTemplateFileName(this.name, this.takenFileNames);
  }

  /** Shown once something is typed; an empty field only keeps the button disabled. */
  get nameError(): string | null {
    const check = this.nameCheck;
    return "problem" in check && this.name.trim() !== ""
      ? this.$t(`admin.pdfTemplates.files.problems.${check.problem}`).toString()
      : null;
  }

  get fileError(): string | null {
    return this.file !== null && this.file.size > MAX_TEMPLATE_FILE_BYTES
      ? this.$t("admin.pdfTemplates.files.errors.FILE_TOO_LARGE").toString()
      : null;
  }

  get canConfirm(): boolean {
    const nameOk = "fileName" in this.nameCheck;
    const fileOk = !this.takesFile || (this.file !== null && this.fileError === null);
    const sourceOk = this.mode !== "copy" || this.sources.length === 0 || this.sourceId !== null;
    return nameOk && fileOk && sourceOk;
  }

  /** "Phase Schadenaufnahme · nur bei Versicherer „allianz“ · mit Bildern (+Bild)". */
  get switchesLabel(): string {
    const switches = templateNameSwitches(this.name);
    const key = (suffix: string) => `admin.pdfTemplates.files.switches.${suffix}`;

    if (switches.hiddenInPicker) {
      return this.$t(key("hidden")).toString();
    }

    const parts = switches.alwaysShown
      ? [this.$t(key("always")).toString()]
      : [
          switches.phase
            ? this.$t(key("phase"), [this.$t(key(`phases.${switches.phase}`)).toString()]).toString()
            : this.$t(key("noPhase")).toString(),
          ...(switches.insurer ? [this.$t(key("insurer"), [switches.insurer]).toString()] : []),
        ];
    if (switches.withImages) {
      parts.push(this.$t(key("images")).toString());
    }
    return parts.join(" · ");
  }

  onFileChanged(file: File | null) {
    this.file = file ?? null;

    // A new upload starts from the PDF's own name; a replace keeps what is typed.
    if (file && this.mode === "upload" && this.name.trim() === "") {
      this.name = file.name.replace(/\.pdf$/i, "");
    }
  }

  onSourceChanged(sourceId: string | null) {
    this.sourceId = sourceId;
    const source = this.sources.find((candidate) => candidate.id === sourceId);
    if (source) {
      this.name = source.fileName.replace(/\.pdf$/i, "");
    }
  }

  onConfirm() {
    const check = this.nameCheck;
    if (!this.canConfirm || !("fileName" in check)) {
      return;
    }
    this.close({ fileName: check.fileName, file: this.takesFile ? this.file : null, sourceId: this.sourceId });
  }

  /** BaseDialog has already closed itself on ABBRECHEN. */
  onCancel() {
    this.resolve?.(null);
    this.resolve = null;
  }

  close(result: PdfTemplateFileDialogResult | null) {
    this.baseDialog.isShowing = false;
    this.resolve?.(result);
    this.resolve = null;
  }
}
